<template>
  <div class="adgroup-reach campaign-step">
    <div class="step-header">
      <div class="step-icon"><img src="/images/globe-lock.svg" alt="reach" /></div>
      <h2>{{ $t('messages.reach_brand_safety_adgroup') || 'Reach & Brand Safety' }}</h2>
    </div>
    <div class="settings-content">
      <frequency-settings :value="localReach" @update="onFreqUpdate" />

      <div style="margin-top:12px;">
        <template v-if="creativeType !== null && creativeType == 'Meta'">
          <placement-segments :selected="selectedPlacements" :placements="placements"
            @update:placements="onUpdatePlacements" />
        </template>
        <template v-else>
          <blocklist type="black" :blocklists="blocklists" :selected-blocklists="selectedBlacklists"
            :disabled="selectedWhitelists && selectedWhitelists.length > 0"
            @toggle-blocklist="onToggleBlack" @blocklist-created="onBlocklistCreated"
            @blocklist-deleted="onBlocklistDeleted" />
          <blocklist type="white" :blocklists="blocklists" :selected-blocklists="selectedWhitelists"
            :disabled="selectedBlacklists && selectedBlacklists.length > 0"
            @toggle-blocklist="onToggleWhite" @blocklist-created="onBlocklistCreated"
            @blocklist-deleted="onBlocklistDeleted" />
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import FrequencySettings from './ReachBrandSafety/FrequencySettings.vue';
import Blocklist from './ReachBrandSafety/Blocklist.vue';
import PlacementSegments from './ReachBrandSafety/PlacementSegments.vue';

export default {
  name: 'AdGroupReachBrandSafety',
  components: { FrequencySettings, Blocklist, PlacementSegments },
  emits: ['update'],
  props: {
    creativeType: {
      type: String,
      default: ''
    },
    placements: {
      type: Array,
      default: () => []
    },
    initialReach: {
      type: Object,
      default: null
    },
    blocklists: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedBlacklists: [],
      selectedWhitelists: [],
      selectedPlacements: [],
      localReach: {
        frequency: null,
        period_type: 'day',
        period_value: 1,
        frequency_is_active: true
      }
    };
  },
  mounted() {
    // initialize from parent-provided data (edit mode) without emitting updates
    this.applyInitialReach(this.initialReach);
  },
  watch: {
    // Re-initialize when parent supplies initialReach after mount (e.g., async populate)
    initialReach: {
      handler(newVal) {
        this.applyInitialReach(newVal);
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    applyInitialReach(ir) {
      const useServerList = Array.isArray(this.blocklists) && this.blocklists.length;
      if (ir && typeof ir === 'object') {
        this.localReach = {
          frequency: ir.frequency ?? this.localReach.frequency,
          period_type: ir.period_type ?? this.localReach.period_type,
          period_value: ir.period_value ?? this.localReach.period_value,
          frequency_is_active: !!ir.frequency_is_active
        };
        // normalize incoming meta_placements to numeric ids
        if (Array.isArray(ir.meta_placements)) {
          this.selectedPlacements = ir.meta_placements.map(v => (typeof v === 'object' && v !== null && 'id' in v) ? Number(v.id) : Number(v)).filter(Boolean);
        } else {
          this.selectedPlacements = [];
        }
        if (useServerList) {
          if (Array.isArray(ir.blocklists) && ir.blocklists.length) {
            this.selectedBlacklists = this.blocklists.filter(b => ir.blocklists.includes(b.id));
          }
          if (Array.isArray(ir.whitelists) && ir.whitelists.length) {
            this.selectedWhitelists = this.blocklists.filter(b => ir.whitelists.includes(b.id));
          }
        }
      } else {
        if (useServerList) {
          this.selectedBlacklists = this.blocklists;
          this.selectedWhitelists = this.blocklists;
        }
      }
      // Do not emit during initialization — parent is the single source of truth.
    },
    // Note: blocklists are provided by server via `blocklists` prop.
    // No API fetch is performed here; created/deleted blocklists are handled via events.
    // handler for placement selector updates
    onUpdatePlacements(selectedIds) {
      this.selectedPlacements = Array.isArray(selectedIds) ? selectedIds : [];
      this.updateData();
    },
    onFreqUpdate(data) {
      this.localReach = { ...this.localReach, ...data };
      this.updateData();
    },
    onToggleBlack(blocklist) {
      const idx = this.selectedBlacklists.findIndex(b => b.id === blocklist.id);
      if (idx === -1) this.selectedBlacklists.push(blocklist);
      else this.selectedBlacklists.splice(idx, 1);
      this.updateData();
    },
    onToggleWhite(blocklist) {
      const idx = this.selectedWhitelists.findIndex(b => b.id === blocklist.id);
      if (idx === -1) this.selectedWhitelists.push(blocklist);
      else this.selectedWhitelists.splice(idx, 1);
      this.updateData();
    },
    onBlocklistCreated(item) {
      // prepend and keep selections intact
      this.selectedBlacklists.push(item);
      this.selectedWhitelists.push(item);
    },
    onBlocklistDeleted(item) {
      let idx = this.selectedBlacklists.findIndex(b => b.id === item.id);
      if (idx !== -1) this.selectedBlacklists.splice(idx, 1);
      idx = this.selectedWhitelists.findIndex(b => b.id === item.id);
      if (idx !== -1) this.selectedWhitelists.splice(idx, 1);
      this.updateData();
    },
    updateData() {
      const dataToEmit = {
        frequency: this.localReach.frequency,
        period_type: this.localReach.period_type,
        period_value: this.localReach.period_value,
        frequency_is_active: this.localReach.frequency_is_active,
        blocklists: this.selectedBlacklists.map(b => b.id),
        whitelists: this.selectedWhitelists.map(b => b.id),
        meta_placements: this.selectedPlacements.slice()
      };
      this.$emit('update', dataToEmit);
    },
    // compatibility helper used by CampaignCreate to read current frequency synchronously
    getCurrentFrequency() {
      return {
        frequency: this.localReach.frequency,
        period_type: this.localReach.period_type,
        period_value: this.localReach.period_value,
        frequency_is_active: this.localReach.frequency_is_active
      };
    }
  }
};
</script>

<style scoped>
.adgroup-reach {
  padding: 8px 0;
}
</style>
